import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/public-pooja-booking.css";


const API_BASE = "https://webcontrol.suvidham.in/api";

const today = () => new Date().toISOString().slice(0, 10);

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const BIRTH_STARS = [
  "Ashwathi / അശ്വതി",
  "Bharani / ഭരണി",
  "Karthika / കാർത്തിക",
  "Rohini / രോഹിണി",
  "Makayiram / മകയിരം",
  "Thiruvathira / തിരുവാതിര",
  "Punartham / പുണർതം",
  "Pooyam / പൂയം",
  "Ayilyam / ആയില്യം",
  "Makam / മകം",
  "Pooram / പൂരം",
  "Uthram / ഉത്രം",
  "Atham / അത്തം",
  "Chithira / ചിത്തിര",
  "Chothi / ചോതി",
  "Vishakham / വിശാഖം",
  "Anizham / അനിഴം",
  "Thrikketta / തൃക്കേട്ട",
  "Moolam / മൂലം",
  "Pooradam / പൂരാടം",
  "Uthradam / ഉത്രാടം",
  "Thiruvonam / തിരുവോണം",
  "Avittam / അവിട്ടം",
  "Chathayam / ചതയം",
  "Pooruruttathi / പൂരുരുട്ടാതി",
  "Uthrattathi / ഉത്രട്ടാതി",
  "Revathi / രേവതി",
];

const emptyDevotee = () => ({
  devotee_name: "",
  birth_star: "",
  pooja_date: today(),
  thidhi: "",
  quantity: 1,
});

export default function PoojaSelection() {
  const nav = useNavigate();

  /* ============================================================
     STATE
  ============================================================ */

  const [data, setData] = useState([]);

  const [active, setActive] = useState(null);

  const [cart, setCart] = useState(() =>
    JSON.parse(localStorage.getItem("pooja_cart") || "[]")
  );

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /*
   * Temporary selected Vazhipads.
   *
   * IMPORTANT:
   * These are NOT yet saved into pooja_cart.
   *
   * User can select many Vazhipads first.
   */
  const [selectedVazhipads, setSelectedVazhipads] =
    useState([]);

  /*
   * Common devotee details that will be applied
   * to all currently selected Vazhipads.
   */
  const [devotee, setDevotee] =
    useState(emptyDevotee());

  const [formError, setFormError] = useState("");

  /* ============================================================
     LOAD PRATHISHTA + VAZHIPAD
  ============================================================ */

  useEffect(() => {
    fetch(`${API_BASE}/public_pooja_catalog.php`)
      .then((response) => response.json())

      .then((result) => {
        if (result.status !== "success") {
          throw new Error(
            result.message ||
            "Unable to load poojas"
          );
        }

        setData(result.data || []);

        if (result.data?.length) {
          setActive(result.data[0].id);
        }
      })

      .catch((err) => {
        setError(err.message);
      })

      .finally(() => {
        setLoading(false);
      });
  }, []);

  /* ============================================================
     SAVE FINAL CART
  ============================================================ */

  useEffect(() => {
    localStorage.setItem(
      "pooja_cart",
      JSON.stringify(cart)
    );
  }, [cart]);

  /* ============================================================
     CURRENT PRATHISHTA
  ============================================================ */

  const deity = data.find(
    (item) =>
      Number(item.id) === Number(active)
  );

  /* ============================================================
     CART TOTAL
  ============================================================ */

  const total = useMemo(() => {
    return cart.reduce(
      (sum, item) =>
        sum +
        Number(item.unit_rate || 0) *
        Number(item.quantity || 1),
      0
    );
  }, [cart]);

  /* ============================================================
     TEMPORARY SELECTED TOTAL
  ============================================================ */

  const selectedTotal = useMemo(() => {
    return selectedVazhipads.reduce(
      (sum, item) =>
        sum +
        Number(item.vazhipad.rate || 0) *
        Number(devotee.quantity || 1),
      0
    );
  }, [
    selectedVazhipads,
    devotee.quantity,
  ]);

  /* ============================================================
     CHECK WHETHER VAZHIPAD IS TEMPORARILY SELECTED
  ============================================================ */

  const isSelected = (
    prathishtaId,
    vazhipadId
  ) => {
    return selectedVazhipads.some(
      (item) =>
        Number(item.deity.id) ===
        Number(prathishtaId) &&
        Number(item.vazhipad.id) ===
        Number(vazhipadId)
    );
  };

  /* ============================================================
     SELECT / DESELECT VAZHIPAD

     Clicking card OR Select button calls this.
  ============================================================ */

  const toggleVazhipad = (
    vazhipad,
    selectedDeity = deity
  ) => {
    if (!selectedDeity) return;

    setFormError("");

    const alreadySelected = isSelected(
      selectedDeity.id,
      vazhipad.id
    );

    if (alreadySelected) {
      setSelectedVazhipads((old) =>
        old.filter(
          (item) =>
            !(
              Number(item.deity.id) ===
              Number(selectedDeity.id) &&
              Number(item.vazhipad.id) ===
              Number(vazhipad.id)
            )
        )
      );

      return;
    }

    setSelectedVazhipads((old) => [
      ...old,

      {
        deity: selectedDeity,
        vazhipad,
      },
    ]);
  };

  /* ============================================================
     REMOVE FROM TEMPORARY SELECTION
  ============================================================ */

  const removeTemporary = (
    prathishtaId,
    vazhipadId
  ) => {
    setSelectedVazhipads((old) =>
      old.filter(
        (item) =>
          !(
            Number(item.deity.id) ===
            Number(prathishtaId) &&
            Number(item.vazhipad.id) ===
            Number(vazhipadId)
          )
      )
    );
  };

  /* ============================================================
     CANCEL CURRENT TEMPORARY SELECTION
  ============================================================ */

  const cancelSelection = () => {
    setSelectedVazhipads([]);
    setDevotee(emptyDevotee());
    setFormError("");
  };

  /* ============================================================
     SAVE ALL SELECTED VAZHIPADS

     All selected Vazhipads receive the SAME:
     - Name
     - Birth Star
     - Date
     - Thidhi
     - Quantity
  ============================================================ */

  const saveSelectedVazhipads = () => {
    setFormError("");

    if (!selectedVazhipads.length) {
      return;
    }

    if (!devotee.devotee_name.trim()) {
      setFormError(
        "Please enter devotee name."
      );

      return;
    }

    if (!devotee.birth_star) {
      setFormError(
        "Please select birth star."
      );

      return;
    }

    if (!devotee.pooja_date) {
      setFormError(
        "Please select pooja date."
      );

      return;
    }

    const quantity = Math.max(
      1,
      Math.min(
        999,
        Number(devotee.quantity) || 1
      )
    );

    /*
     * Convert every temporary selection
     * into a proper pooja_cart item.
     */
    const newItems =
      selectedVazhipads.map(
        ({ deity: d, vazhipad: v }) => {
          const unique = `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}-${v.id}`;

          return {
            key: `${d.id}-${v.id}-${unique}`,

            prathishta_id: Number(d.id),

            prathishta_name:
              d.name_en,

            prathishta_name_regional:
              d.name_regional || "",

            vazhipad_id: Number(v.id),

            vazhipad_name:
              v.name_en,

            vazhipad_name_regional:
              v.name_regional || "",

            unit_rate: Number(v.rate),

            allow_rate_change: Number(
              v.allow_rate_change || 0
            ),

            quantity,

            pooja_date:
              devotee.pooja_date,

            devotee_name:
              devotee.devotee_name.trim(),

            birth_star:
              devotee.birth_star,

            thidhi:
              devotee.thidhi.trim(),

            date_mode:
              v.date_mode,

            single_date:
              v.single_date,

            range_start:
              v.range_start,

            range_end:
              v.range_end,
          };
        }
      );

    setCart((old) => [
      ...old,
      ...newItems,
    ]);

    /*
     * Clear temporary selection.
     *
     * User can now select another group
     * for another person.
     */
    setSelectedVazhipads([]);

    setDevotee(emptyDevotee());

    setFormError("");
  };

  /* ============================================================
     LOADING
  ============================================================ */

  if (loading) {
    return (
      <div className="pb-state">
        Loading poojas…
      </div>
    );
  }

  /* ============================================================
     UI
  ============================================================ */

  return (
    <div className="pb-page pb-page-with-sticky">

      {/* ========================================================
          HERO
      ======================================================== */}

      {/* <section className="pb-hero">

        <div>
          <span className="pb-kicker">
            ONLINE POOJA BOOKING
          </span>

          <h1>
            Select Prathishta & Vazhipads
          </h1>

          <p>
            Select one or more Vazhipads,
            enter the devotee details once,
            and save them together.
          </p>
        </div>

        <div className="pb-total">

          <small>
            Current total
          </small>

          <strong>
            {money(total)}
          </strong>

          <span>
            {cart.length} saved{" "}
            {cart.length === 1
              ? "item"
              : "items"}
          </span>

        </div>

      </section> */}

      {/* ========================================================
          ERROR
      ======================================================== */}

      {error && (
        <div className="pb-error">
          {error}
        </div>
      )}

      {/* ========================================================
          PRATHISHTA
      ======================================================== */}
      <div className="row">
        <div className="col-md-4">
          <h2 className="pb-title">
            Choose Prathishta
          </h2>
          <div className="pb-deities">

        {data.map((prathishta) => (

          <button
            key={prathishta.id}
            className={`pb-deity ${Number(active) ===
                Number(prathishta.id)
                ? "active"
                : ""
              }`}
            onClick={() => {
              /*
               * IMPORTANT:
               * Do NOT clear selectedVazhipads here.
               *
               * This allows the same devotee to
               * select Vazhipads from multiple
               * Prathishtas.
               */
              setActive(prathishta.id);
            }}
          >

            {prathishta.image_path ? (

              <img
                src={`${API_BASE}/${String(
                  prathishta.image_path
                ).replace(/^\//, "")}`}
                alt={prathishta.name_en}
              />

            ) : (

              <div className="pb-image-fallback">
                ॐ
              </div>

            )}

            <div>

              <strong>
                {prathishta.name_en}
              </strong>

              <span>
                {prathishta.name_regional}
              </span>

            </div>

          </button>

        ))}

      </div>
        </div>
        <div className="col-md-8">
          {deity && (
        <>

          <div className="pb-section-head">

            <div>
              <h4 className="gradient-text">
                {deity.name_en}
              </h4>

              <p>
                {deity.name_regional}
              </p>
            </div>

            <span>
              {deity.vazhipads?.length || 0}{" "}
              vazhipads
            </span>

          </div>

          <div className="pb-vazhipads">

            {(deity.vazhipads || []).map(
              (vazhipad) => {
                const selected = isSelected(
                  deity.id,
                  vazhipad.id
                );

                return (
                  <article
                    key={vazhipad.id}
                    role="button"
                    tabIndex="0"
                    className={`pb-v-card ${selected
                        ? "selected pb-temp-selected"
                        : ""
                      }`}
                    onClick={() =>
                      toggleVazhipad(
                        vazhipad,
                        deity
                      )
                    }
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === " "
                      ) {
                        event.preventDefault();

                        toggleVazhipad(
                          vazhipad,
                          deity
                        );
                      }
                    }}
                  >

                    {/* ICON */}

                    <div className="pb-v-icon">

                      {selected ? (
                        <i className="fa fa-check" />
                      ) : (
                        <i className="fa fa-bell-o" />
                      )}

                    </div>

                    {/* DETAILS */}

                    <div className="pb-v-body">

                      <h3>
                        {vazhipad.name_en}
                      </h3>

                      <p className="pb-regional">
                        {vazhipad.name_regional}
                      </p>

                      {vazhipad.information && (
                        <p className="pb-info">
                          {vazhipad.information}
                        </p>
                      )}

                      <div className="pb-rate">
                        {money(vazhipad.rate)}
                      </div>

                      {selected && (
                        <span className="pb-added-badge">
                          Selected
                        </span>
                      )}

                    </div>

                    {/* SELECT BUTTON */}

                    <button
                      type="button"
                      className={`pb-add ${selected
                          ? "pb-add-selected"
                          : ""
                        }`}
                      onClick={(event) => {
                        /*
                         * Prevent article onClick
                         * from firing as well.
                         */
                        event.stopPropagation();

                        toggleVazhipad(
                          vazhipad,
                          deity
                        );
                      }}
                    >

                      {selected ? (
                        <>
                          <i className="fa fa-check" />
                          {" "}
                          Selected
                        </>
                      ) : (
                        "Select"
                      )}

                    </button>

                  </article>
                );
              }
            )}

          </div>

        </>
      )}
        </div>

      </div>


      

      {/* ========================================================
          VAZHIPADS
      ======================================================== */}

      

      {/* ========================================================
          MULTI VAZHIPAD DEVOTEE DOCK

          Appears whenever at least one Vazhipad
          has been selected.

          This is the ONLY panel below the list.
          The old mini-review has been removed.
      ======================================================== */}

      {selectedVazhipads.length > 0 && (

        <section className="pb-devotee-dock">

          {/* HEADER */}

          <div className="pb-dock-head">

            <div>

              <small>
                BOOKING FOR DEVOTEE
              </small>

              <h3>
                {selectedVazhipads.length}{" "}
                Vazhipad
                {selectedVazhipads.length === 1
                  ? ""
                  : "s"}{" "}
                Selected
              </h3>

            </div>

            <button
              type="button"
              className="pb-dock-close"
              onClick={cancelSelection}
              title="Cancel current selection"
            >
              ×
            </button>

          </div>

          {/* ==================================================
              SELECTED VAZHIPAD CHIPS
          ================================================== */}

          <div className="pb-selected-vazhipads">

            {selectedVazhipads.map(
              ({ deity: d, vazhipad: v }) => (

                <div
                  className="pb-selected-chip"
                  key={`${d.id}-${v.id}`}
                >

                  <div>

                    <strong>
                      {v.name_en}
                    </strong>

                    <span>
                      {d.name_en} •{" "}
                      {money(v.rate)}
                    </span>

                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      removeTemporary(
                        d.id,
                        v.id
                      )
                    }
                    title="Remove selection"
                  >
                    ×
                  </button>

                </div>

              )
            )}

          </div>

          {/* ERROR */}

          {formError && (
            <div className="pb-inline-error">
              {formError}
            </div>
          )}

          {/* ==================================================
              DEVOTEE FIELDS
          ================================================== */}

          <div className="pb-dock-fields">

            {/* NAME */}

            <label>

              Name *

              <input
                autoFocus
                value={
                  devotee.devotee_name
                }
                maxLength="150"
                onChange={(event) =>
                  setDevotee({
                    ...devotee,

                    devotee_name:
                      event.target.value,
                  })
                }
                placeholder="Devotee name"
              />

            </label>

            {/* BIRTH STAR */}

            <label>

              Birth Star *

              <select
                value={
                  devotee.birth_star
                }
                onChange={(event) =>
                  setDevotee({
                    ...devotee,

                    birth_star:
                      event.target.value,
                  })
                }
              >

                <option value="">
                  Select birth star
                </option>

                {BIRTH_STARS.map(
                  (star) => (
                    <option
                      key={star}
                      value={star}
                    >
                      {star}
                    </option>
                  )
                )}

              </select>

            </label>

            {/* DATE */}

            <label>

              Pooja Date *

              <input
                type="date"
                min={today()}
                value={
                  devotee.pooja_date
                }
                onChange={(event) =>
                  setDevotee({
                    ...devotee,

                    pooja_date:
                      event.target.value,
                  })
                }
              />

            </label>

            {/* THIDHI */}

            <label>

              Thidhi

              <input
                value={devotee.thidhi}
                maxLength="100"
                onChange={(event) =>
                  setDevotee({
                    ...devotee,

                    thidhi:
                      event.target.value,
                  })
                }
                placeholder="Optional"
              />

            </label>

            {/* QUANTITY */}

            <label>

              Quantity

              <input
                type="number"
                min="1"
                max="999"
                value={
                  devotee.quantity
                }
                onChange={(event) =>
                  setDevotee({
                    ...devotee,

                    quantity:
                      event.target.value,
                  })
                }
              />

            </label>

          </div>

          {/* ==================================================
              TEMPORARY TOTAL + SAVE
          ================================================== */}

          <div className="pb-dock-footer">

            <div className="pb-selection-total">

              <small>
                Selected Amount
              </small>

              <strong>
                {money(selectedTotal)}
              </strong>

            </div>

            <button
              type="button"
              className="pb-save-devotee"
              onClick={
                saveSelectedVazhipads
              }
            >

              <i className="fa fa-check" />

              {" "}

              Save{" "}

              {
                selectedVazhipads.length
              }{" "}

              Vazhipad
              {
                selectedVazhipads.length ===
                  1
                  ? ""
                  : "s"
              }

            </button>

          </div>

        </section>

      )}

      {/* ========================================================
          STICKY BOTTOM

          No review/mini-cart is rendered above this anymore.
      ======================================================== */}

      <div className="pb-sticky">

        <div>

          <small>
            Total
          </small>

          <strong>
            {money(total)}
          </strong>

          <span>
            {cart.length} saved
          </span>

        </div>

        <button
          disabled={
            !cart.length ||
            selectedVazhipads.length > 0
          }
          onClick={() =>
            nav("/pooja-review")
          }
        >

          Proceed

          {" "}

          <i className="fa fa-arrow-right" />

        </button>

      </div>

    </div>
  );
}